// ─────────────────────────────────────────────────────────────────────────────
// HOW TO ADD A NEW INTEGRATION
// ─────────────────────────────────────────────────────────────────────────────
// 1. Add your entry to the `applications` array below with a unique string id.
// 2. Add EXACTLY ONE entry to integrationGateMap in IntegrationsLanding.tsx.
//    TypeScript WILL NOT COMPILE if you skip this step — AppId is derived from
//    this array and used as the Record key type of integrationGateMap.
// 3. Add a route in Routes.tsx (back button is automatic via IntegrationPageWrapper).
// 4. If there is a new subscription flag: add it to both Subscription.cs AND
//    SubscriptionsProvider.tsx (see sync-subscription-flags.js for a check script).
// ─────────────────────────────────────────────────────────────────────────────

// Images served from /public/img to avoid Rollup node_modules resolution issues
const SalesforceLogo = "/img/salesforce-crm.svg";
const NetSuiteLogo = "/img/netsuite-crm.svg";
const FormstackLogo = "/img/formstack.svg";
const AccelaLogo = "/img/accela-civic.svg";
const ShopifyLogo = "/img/shopify.svg";
const HubSpotLogo = "/img/hubspot-crm.svg";
const Dynamics365Logo = "/img/ms-dynamics-365.svg";
const BigCommerceLogo = "/img/bigcommerce.svg";
const WooCommerceLogo = "/img/woocommerce.svg";
const McpLogo = "/img/mcp-server.svg";
const PowerAutomateLogo = "/img/ms-power-automate.svg";
const MakeLogo = "/img/make-integromat.svg";
const ZapierLogo = "/img/zapier.svg";
const SlackLogo = "/img/slack.svg";
const TeamsLogo = "/img/ms-teams.svg";
const OutlookCalendarLogo = "/img/outlook-calendar.svg";
const CalendlyLogo = "/img/calendly.svg";
const SquareLogo = "/img/square-appointments.svg";
const KlaviyoLogo = "/img/klaviyo.svg";
const StripeLogo = "/img/stripe.svg";
const GorgiasLogo = "/img/gorgias.svg";
const GoogleTagManagerLogo = "/img/google-tag-manager.svg";
const MagentoLogo = "/img/magento.svg";
const ServiceNowLogo = "/img/service-now.svg";
const SapLogo = "/img/sap.svg";
const Microsoft365Logo = "/img/ms-365.svg";
const MicrosoftBookingsLogo = "/img/ms-bookings.svg";
const TranscriptStorageLogo = "/img/transcript-storage.svg";
const GmailLogo = "/img/gmail.svg";
const OutlookLogo = "/img/outlook.svg";
const UplistingLogo = "/img/uplisting.svg";
const GuestyLogo = "/img/guesty.svg";
const CloudbedsLogo = "/img/cloudbeds.svg";
const PipedriveLogo = "/img/pipedrive.svg";
const QuickBooksLogo = "/img/quickbooks-online.svg";
const ZohoLogo = "/img/zoho-crm.svg";
const ToastLogo = "/img/toast-pos.svg";
const CloverLogo = "/img/clover-pos.svg";
const MindbodyLogo = "/img/mindbody.svg";
const JobberLogo = "/img/jobber.svg";
const SecureFormsLogo = "/img/secureforms.svg";
const VastLogo = "/img/vast-retail.svg";
const RecruitingLogo = "/img/recruiting-bot.svg";
const NextivaLogo = "/img/nextiva.svg";
const OpenTableLogo = "/img/opentable.svg";
const ResyLogo = "/img/resy.svg";
const WebflowLogo = "/img/webflow.svg";
import { IconPlus } from "@velaro/velaro-component-lib/";

export const applications = [
  {
    id: "new",
    name: "Request New App",
    category: "all",
    icon: IconPlus,
    subTitle:
      "Submit your application integration suggestion by completing our request form"
  },
  {
    id: "velaro-crm",
    name: "Velaro CRM",
    category: "all",
    icon: null as any,
    subTitle:
      "Built-in CRM with deals, contacts, companies, buying signal detection, and 24 AI tools — no external account needed."
  },
  {
    id: "netsuite",
    name: "Netsuite CRM",
    category: "all",
    icon: NetSuiteLogo,
    subTitle:
      "Connect directly to manage and streamline your customer communications and data."
  },
  {
    id: "formstack",
    name: "Formstack",
    category: "all",
    icon: FormstackLogo,
    subTitle:
      "Collect, manage, and automate form submissions to enhance workflows and improve data accuracy."
  },
  {
    id: "accela-civic",
    name: "Accela Civic",
    category: "all",
    icon: AccelaLogo,
    subTitle:
      "AI-powered civic engagement — permits, inspections, licensing, 311 service requests, and citizen services."
  },
  {
    id: "shopify",
    name: "Shopify",
    category: "all",
    icon: ShopifyLogo,
    subTitle:
      "Integrate Shopify to sync e-commerce activities with customer profiles directly."
  },
  {
    id: "hubspot",
    name: "HubSpot CRM",
    category: "all",
    icon: HubSpotLogo,
    subTitle:
      "Connect HubSpot to look up contacts, push transcripts, and sync chat data to your CRM."
  },
  {
    id: "salesforce",
    name: "Salesforce CRM",
    category: "all",
    icon: SalesforceLogo,
    subTitle:
      "Connect Salesforce to look up contacts, manage leads and cases, update opportunities, and log notes — all from live chat."
  },
  {
    id: "dynamics",
    name: "Microsoft Dynamics 365",
    category: "all",
    icon: Dynamics365Logo,
    subTitle:
      "Connect Dynamics 365 to look up contacts, create leads and cases, and sync chat data to your CRM."
  },
  {
    id: "bigcommerce",
    name: "BigCommerce",
    category: "all",
    icon: BigCommerceLogo,
    subTitle:
      "Integrate BigCommerce to look up customers, orders, and products during live support chats."
  },
  {
    id: "woocommerce",
    name: "WooCommerce",
    category: "all",
    icon: WooCommerceLogo,
    subTitle:
      "Connect WooCommerce to search products, look up orders, and retrieve customer history during live chats. Supports any WordPress store."
  },
  {
    id: "magento",
    name: "Magento / Adobe Commerce",
    category: "all",
    icon: MagentoLogo,
    subTitle:
      "Connect Magento or Adobe Commerce to look up customers, orders, products, and B2B data during live support chats."
  },
  {
    id: "mcp",
    name: "MCP Servers",
    category: "all",
    icon: McpLogo,
    subTitle:
      "Connect AI workflow nodes to external tools via the Model Context Protocol, with full conversation context awareness."
  },
  {
    id: "power-automate",
    name: "Power Automate",
    category: "automation",
    icon: PowerAutomateLogo,
    subTitle:
      "Trigger Microsoft Power Automate flows from chat conversations and workflow nodes. Bidirectional data exchange."
  },
  {
    id: "make",
    name: "Make (Integromat)",
    category: "automation",
    icon: MakeLogo,
    subTitle:
      "Connect Velaro to 1,000+ apps via Make.com. Trigger scenarios from workflows, send conversation data, and return variables."
  },
  {
    id: "zapier",
    name: "Zapier",
    category: "automation",
    icon: ZapierLogo,
    subTitle:
      "Trigger Zapier Zaps when conversations start, end, or hit milestones. Automate CRM updates, tickets, and notifications."
  },
  {
    id: "slack-notifications",
    name: "Slack Notifications",
    category: "notifications",
    icon: SlackLogo,
    subTitle:
      "Send real-time alerts to Slack channels when conversations resolve, are missed, or breach SLA. Configure per-channel webhooks with custom trigger rules."
  },
  {
    id: "teams-notifications",
    name: "Teams Notifications",
    category: "notifications",
    icon: TeamsLogo,
    subTitle:
      "Send Adaptive Card alerts to Microsoft Teams channels when conversations resolve, arrive, or breach SLA. No Microsoft 365 account required."
  },
  {
    id: "alert-rules",
    name: "Alert Rules",
    category: "notifications",
    icon: null as any,
    subTitle:
      "Get notified directly via SMS, email, or mobile push when queue conditions exceed thresholds. No Slack or Teams account required."
  },
  {
    id: "outlook-calendar",
    name: "Outlook Calendar",
    category: "scheduling",
    icon: OutlookCalendarLogo,
    subTitle:
      "Book meetings directly on an Outlook calendar during chat. Send calendar invites, collect preferred times, and confirm bookings — all from the bot."
  },
  {
    id: "calendly",
    name: "Calendly",
    category: "scheduling",
    icon: CalendlyLogo,
    subTitle:
      "Send personalized Calendly booking links to visitors. Let them self-schedule demos, support calls, and appointments without leaving the chat."
  },
  {
    id: "square-appointments",
    name: "Square Appointments",
    category: "scheduling",
    icon: SquareLogo,
    subTitle:
      "Let visitors book appointments via SMS, WhatsApp, or website chat — powered by your Square calendar. No forms, no links."
  },
  {
    id: "stripe-payments",
    name: "Stripe Payments",
    category: "automation",
    icon: StripeLogo,
    subTitle:
      "Send payment requests inside any workflow. Customers pay via Apple Pay, Google Pay, or card — without leaving the chat. Powered by Stripe Checkout."
  },
  {
    id: "klaviyo",
    name: "Klaviyo",
    category: "automation",
    icon: KlaviyoLogo,
    subTitle:
      "Trigger Klaviyo email and SMS flows after conversations resolve. Power post-chat follow-ups, CSAT surveys, and abandoned chat recovery — automatically."
  },
  {
    id: "gorgias",
    name: "Migrate from Gorgias",
    category: "all",
    icon: GorgiasLogo,
    subTitle:
      "Switch from Gorgias to Velaro in 15 minutes. Import your macros as bot Q&A, connect Shopify, and launch an AI that handles support automatically — at half the price."
  },
  {
    id: "google-tag-manager",
    name: "Google Tag Manager",
    category: "all",
    icon: GoogleTagManagerLogo,
    subTitle:
      "Deploy Velaro without code via GTM. Push chat events (chat_started, conversation_resolved) to GA4, Google Ads, and Meta Pixel. Pass visitor variables into the bot."
  },
  {
    id: "microsoft-365",
    name: "Microsoft 365",
    category: "all",
    icon: Microsoft365Logo,
    subTitle:
      "Connect Microsoft 365 to enable OneDrive file attachments in chat, Teams channel notifications, and Outlook Calendar booking."
  },
  {
    id: "transcript-storage",
    name: "Transcript Storage",
    category: "all",
    icon: TranscriptStorageLogo,
    subTitle:
      "Archive every conversation transcript to Azure Blob, AWS S3, SharePoint, or a custom HTTPS webhook for compliance and data retention."
  },
  {
    id: "servicenow",
    name: "ServiceNow ITSM",
    category: "all",
    icon: ServiceNowLogo,
    subTitle:
      "Connect ServiceNow to create incidents, search the knowledge base, look up users and CI records, manage service requests, and log work notes — all from chat or IVR."
  },
  {
    id: "sap",
    name: "SAP Business One",
    category: "all",
    icon: SapLogo,
    subTitle:
      "Connect SAP to search customers, look up sales orders and AR invoices, check real-time inventory, and manage service calls — all from live chat or IVR."
  },
  {
    id: "uplisting",
    name: "Uplisting",
    category: "all",
    icon: UplistingLogo,
    subTitle:
      "Connect Uplisting to answer guest questions, check availability, manage bookings, send check-in instructions, and handle maintenance requests — all from chat."
  },
  {
    id: "guesty",
    name: "Guesty",
    category: "all",
    icon: GuestyLogo,
    subTitle:
      "Connect Guesty to manage reservations, send guest messages, generate owner statements, coordinate housekeeping, and handle the full rental lifecycle from chat."
  },
  {
    id: "cloudbeds",
    name: "Cloudbeds",
    category: "all",
    icon: CloudbedsLogo,
    subTitle:
      "Connect Cloudbeds to look up reservations, process check-ins, manage folios, coordinate housekeeping, and handle billing — all from chat or IVR."
  },
  {
    id: "pipedrive",
    name: "Pipedrive CRM",
    category: "all",
    icon: PipedriveLogo,
    subTitle:
      "Connect Pipedrive to look up contacts, manage deals, log activities, and sync chat conversations to your pipeline — automatically."
  },
  {
    id: "quickbooks",
    name: "QuickBooks Online",
    category: "all",
    icon: QuickBooksLogo,
    subTitle:
      "Connect QuickBooks to let customers check invoices, payments, and account status directly in chat. 20 AI tools for customers, invoices, payments, and estimates."
  },
  {
    id: "zoho",
    name: "Zoho CRM",
    category: "all",
    icon: ZohoLogo,
    subTitle:
      "Connect Zoho CRM to look up contacts, leads, and deals, log chat activities, and manage your pipeline — all from live chat."
  },
  {
    id: "toast",
    name: "Toast POS",
    category: "all",
    icon: ToastLogo,
    subTitle:
      "Connect Toast to answer menu questions, check order status, confirm hours, and handle restaurant inquiries automatically — 24/7."
  },
  {
    id: "clover",
    name: "Clover POS",
    category: "all",
    icon: CloverLogo,
    subTitle:
      "Connect Clover POS to answer item questions, check order status, look up customers, and share store hours — all from chat, 24/7."
  },
  {
    id: "mindbody",
    name: "Mindbody",
    category: "all",
    icon: MindbodyLogo,
    subTitle:
      "Connect Mindbody so members can book classes, check schedules, cancel sessions, and manage their account — all from chat, 24/7."
  },
  {
    id: "recruiting",
    name: "Recruiting Bot",
    category: "all",
    icon: RecruitingLogo,
    subTitle:
      "Connect your ATS or careers feed so the bot can surface open roles, answer candidate questions, and send qualified leads directly to your recruiter — 24/7."
  },
  {
    id: "jobber",
    name: "Jobber",
    category: "all",
    icon: JobberLogo,
    subTitle:
      "Connect Jobber so clients can check job status, view quotes, review invoices, and submit service requests automatically — perfect for HVAC, plumbing, electrical, landscaping, and any field service business."
  },
  {
    id: "microsoft-bookings",
    name: "Microsoft Bookings",
    category: "scheduling",
    icon: MicrosoftBookingsLogo,
    subTitle:
      "Let your AI bot check availability, book appointments, look up services and staff — powered by Microsoft Bookings. Requires an active Microsoft 365 connection."
  },
  {
    id: "secure-forms",
    name: "SecureForms",
    category: "all",
    icon: SecureFormsLogo,
    subTitle:
      "Collect and process secure form submissions — HIPAA-compliant intake forms, applications, and data collection. Webhook callbacks trigger Velaro workflows automatically."
  },
  {
    id: "vast",
    name: "VAST Retail",
    category: "all",
    icon: VastLogo,
    subTitle:
      "Connect VAST Retail (Kerridge CS) to look up customers, search tire inventory, check OEM sizing, book appointments, and check work order status — all from chat or IVR."
  },
  {
    id: "nextiva",
    name: "Nextiva",
    category: "voice",
    icon: NextivaLogo,
    subTitle:
      "Connect Nextiva to get a customer screen pop on every inbound call — automatically pulls VAST customer record, vehicle, and service history when a call comes in."
  },
  {
    id: "gmail-inbox",
    name: "Gmail Inbox",
    category: "email",
    icon: GmailLogo,
    subTitle:
      "Connect Gmail or Google Workspace mailboxes so every inbound email becomes a tracked ticket. Supports individual OAuth and Domain-Wide Delegation for IT admins."
  },
  {
    id: "outlook-inbox",
    name: "Outlook Inbox",
    category: "email",
    icon: OutlookLogo,
    subTitle:
      "Connect Microsoft 365 or Exchange mailboxes so every inbound email becomes a tracked ticket. Real-time delivery via Microsoft Graph change notifications."
  },
  {
    id: "opentable",
    name: "OpenTable",
    category: "scheduling",
    icon: OpenTableLogo,
    subTitle:
      "Let customers book restaurant reservations by phone, chat, or SMS — powered by OpenTable. Checks availability, takes party size, confirms instantly."
  },
  {
    id: "resy",
    name: "Resy",
    category: "scheduling",
    icon: ResyLogo,
    subTitle:
      "Connect Resy to let customers find open tables, book reservations, and receive confirmation by text — all from a single conversation."
  },

  // ── Website Builder ─────────────────────────────────────────────────────
  {
    id: "webflow",
    name: "Webflow",
    category: "all",
    icon: WebflowLogo,
    subTitle:
      "Connect your Webflow site and deploy the Velaro chat widget with one click — no code required. Monitor form submissions and engage visitors in real time."
  }
];

/**
 * Union type of every integration app ID.
 *
 * This type is used as the key type for `integrationGateMap` in IntegrationsLanding.tsx.
 * TypeScript enforces that EVERY app ID in this list has an entry in the gate map.
 * If you add an app here and forget to add it to integrationGateMap, `tsc` will fail
 * with: "Type 'Record<string, ...>' is missing the following properties: 'your-new-id'"
 *
 * That compile error IS the enforcement mechanism. Do not work around it.
 */
export type AppId = (typeof applications)[number]["id"];

/**
 * Marketplace availability for each integration.
 *
 * Used by UnlockIntegrationPanel to show customers the correct upgrade path:
 *   - marketplace customers (Shopify, BigCommerce, WooCommerce) → upgrade on that store
 *   - direct Velaro customers → contact account manager
 *
 * HOW TO ADD A NEW MARKETPLACE LISTING:
 *   1. Publish the app/plugin to the marketplace.
 *   2. Add the app ID here with { name, url, iconUrl (optional) }.
 *   3. The UnlockIntegrationPanel picks it up automatically.
 *
 * storeLabel: short name shown on the button ("Shopify App Store", "BigCommerce Apps", etc.)
 * storeUrl:   deep-link to your listing page on that marketplace
 * note:       optional short line shown under the link (e.g. "Requires Velaro on Shopify")
 */
export const integrationMarketplaceLinks: Partial<Record<AppId, {
  storeLabel: string;
  storeUrl: string;
  note?: string;
}[]>> = {
  "shopify": [
    {
      storeLabel: "Shopify App Store",
      storeUrl: "https://apps.shopify.com/velaro",
      note: "If you installed Velaro through Shopify, upgrade your plan there."
    }
  ],
  "bigcommerce": [
    {
      storeLabel: "BigCommerce Marketplace",
      storeUrl: "https://www.bigcommerce.com/apps/velaro/",
      note: "If you installed Velaro through BigCommerce, upgrade your plan there."
    }
  ],
  "woocommerce": [
    {
      storeLabel: "WordPress Plugin Directory",
      storeUrl: "https://wordpress.org/plugins/velaro/",
      note: "If you use Velaro via the WordPress plugin, manage your plan there."
    }
  ],
  // Enterprise integrations available through partner marketplaces
  "salesforce": [
    {
      storeLabel: "Salesforce AppExchange",
      storeUrl: "https://appexchange.salesforce.com/",
      note: "Available on AppExchange for Salesforce orgs."
    }
  ],
  "microsoft-365": [
    {
      storeLabel: "Microsoft AppSource",
      storeUrl: "https://appsource.microsoft.com/",
      note: "Available on Microsoft AppSource."
    }
  ],
  // Add more as listings go live
};
